import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  CheckCircle2, XCircle, Eye, FileText, ImageIcon, Loader2, Clock, RefreshCw, CreditCard
} from 'lucide-react';

interface PaymentProof {
  id: number;
  alumnoId: number;
  alumnoNui: string;
  alumnoNombre: string;
  alumnoApellidoPaterno: string;
  alumnoApellidoMaterno: string;
  archivoNombre: string;
  archivoTipo: string;
  archivoTamano: number;
  referencia: string | null;
  invoiceId: number | null;
  invoiceNumber: string | null;
  status: 'pendiente' | 'validado' | 'rechazado';
  notasAdmin: string | null;
  aiFlagged: boolean;
  aiReason: string | null;
  uploaderName: string | null;
  validatorName: string | null;
  validatedAt: string | null;
  createdAt: string;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'validado', label: 'Validados' },
  { value: 'rechazado', label: 'Rechazados' },
];

const STATUS_CONFIG = {
  pendiente: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
  validado: { label: 'Validado', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rechazado: { label: 'Rechazado', className: 'bg-red-100 text-red-800', icon: XCircle },
};

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileTypeIcon({ tipo }: { tipo: string }) {
  if (tipo === 'application/pdf') return <FileText className="w-5 h-5 text-red-500 shrink-0" />;
  return <ImageIcon className="w-5 h-5 text-blue-500 shrink-0" />;
}

export default function ValidacionComprobantes() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('pendiente');
  const [actionModal, setActionModal] = useState<{ proof: PaymentProof; type: 'validate' | 'reject' } | null>(null);
  const [notasAdmin, setNotasAdmin] = useState('');
  const [recentlyValidated, setRecentlyValidated] = useState<Set<number>>(new Set());

  const { data: proofs = [], isLoading, refetch } = useQuery<PaymentProof[]>({
    queryKey: ['admin-payment-proofs', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const r = await fetch(`/api/payment-proofs?${params}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Error');
      return r.json();
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({ proofId, action, notas }: { proofId: number; action: 'validate' | 'reject'; notas: string }) => {
      const endpoint = action === 'validate' ? 'validate' : 'reject';
      const r = await fetch(`/api/payment-proofs/${proofId}/${endpoint}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notasAdmin: notas || null }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error');
      }
      return r.json();
    },
    onSuccess: (_, { action, proofId }) => {
      toast({
        title: action === 'validate' ? 'Comprobante validado' : 'Comprobante rechazado',
        description: action === 'validate'
          ? 'Comprobante verificado. Ahora ve a "Validación de Pagos" para registrar el pago.'
          : 'El comprobante fue rechazado.',
      });
      if (action === 'validate' && actionModal) {
        setRecentlyValidated(prev => new Set(prev).add(actionModal.proof.alumnoId));
      }
      setActionModal(null);
      setNotasAdmin('');
      queryClient.invalidateQueries({ queryKey: ['admin-payment-proofs'] });
      queryClient.invalidateQueries({ queryKey: ['payment-validation'] });
      queryClient.invalidateQueries({ queryKey: ['payment-proofs-pending-all'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const openAction = (proof: PaymentProof, type: 'validate' | 'reject') => {
    setNotasAdmin('');
    setActionModal({ proof, type });
  };

  const counts = {
    pendiente: proofs.filter(p => p.status === 'pendiente').length,
    total: proofs.length,
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Validación de Comprobantes</h1>
          <p className="text-gray-500 mt-1">
            Revisa y verifica los comprobantes de pago enviados por los alumnos
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Summary pills */}
      <div className="flex gap-3 mb-5 flex-wrap">
        {(['all', 'pendiente', 'validado', 'rechazado'] as const).map((s) => {
          const count = s === 'all'
            ? proofs.length
            : proofs.filter(p => p.status === s).length;
          const cfg = s === 'all'
            ? { label: 'Todos', className: 'bg-gray-100 text-gray-700' }
            : STATUS_CONFIG[s];
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                statusFilter === s
                  ? 'ring-2 ring-offset-1 ring-blue-400 ' + cfg.className
                  : cfg.className + ' opacity-70 hover:opacity-100'
              }`}
            >
              {cfg.label} {count > 0 && <span className="ml-1 font-bold">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Cargando comprobantes...</p>
          </div>
        ) : proofs.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">
              {statusFilter === 'pendiente'
                ? 'No hay comprobantes pendientes de revisión'
                : 'No se encontraron comprobantes'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Alumno</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Archivo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Referencia</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {proofs.map((proof) => {
                  const statusCfg = STATUS_CONFIG[proof.status];
                  const StatusIcon = statusCfg.icon;
                  return (
                    <tr key={proof.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">
                          {proof.alumnoApellidoPaterno} {proof.alumnoApellidoMaterno}, {proof.alumnoNombre}
                        </p>
                        <p className="text-xs text-gray-500">{proof.alumnoNui}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileTypeIcon tipo={proof.archivoTipo} />
                          <div className="min-w-0">
                            <p className="text-xs text-gray-700 truncate max-w-[160px]" title={proof.archivoNombre}>
                              {proof.archivoNombre}
                            </p>
                            <p className="text-xs text-gray-400">{formatBytes(proof.archivoTamano)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600 max-w-[180px]">
                          {proof.invoiceNumber && (
                            <p className="font-medium text-blue-600">{proof.invoiceNumber}</p>
                          )}
                          {proof.referencia && (
                            <p className="text-gray-500 truncate">{proof.referencia}</p>
                          )}
                          {!proof.invoiceNumber && !proof.referencia && (
                            <span className="text-gray-300 italic">Sin referencia</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-700">
                          {new Date(proof.createdAt).toLocaleDateString('es-MX', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}
                        </p>
                        {proof.uploaderName && (
                          <p className="text-xs text-gray-400">{proof.uploaderName}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusCfg.className}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusCfg.label}
                        </span>
                        {proof.aiFlagged && (
                          <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1" title={proof.aiReason ?? undefined}>
                            ⚠️ IA no lo reconoció{proof.aiReason ? `: ${proof.aiReason}` : ''}
                          </p>
                        )}
                        {proof.notasAdmin && (
                          <p className="text-xs text-gray-400 mt-0.5 italic max-w-[140px] truncate" title={proof.notasAdmin}>
                            "{proof.notasAdmin}"
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <a
                            href={`/api/payment-proofs/${proof.id}/file`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Ver
                          </a>
                          {proof.status === 'pendiente' && (
                            <>
                              <button
                                onClick={() => openAction(proof, 'validate')}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-green-100 hover:bg-green-200 text-xs font-medium text-green-700 transition-colors"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Validar
                              </button>
                              <button
                                onClick={() => openAction(proof, 'reject')}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-100 hover:bg-red-200 text-xs font-medium text-red-700 transition-colors"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                                Rechazar
                              </button>
                            </>
                          )}
                          {(proof.status === 'validado' || recentlyValidated.has(proof.alumnoId)) && (
                            <a
                              href="/app/administration/payment-validation"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-xs font-medium text-blue-700 transition-colors"
                              title="Ir a Validación de Pagos para registrar el pago"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              Registrar Pago
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action modal */}
      {actionModal && (
        <Dialog open onOpenChange={() => { setActionModal(null); setNotasAdmin(''); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionModal.type === 'validate' ? '✅ Validar Comprobante' : '❌ Rechazar Comprobante'}
              </DialogTitle>
              <DialogDescription>
                {actionModal.proof.alumnoNui} — {actionModal.proof.alumnoApellidoPaterno} {actionModal.proof.alumnoNombre}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* File preview link */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                <FileTypeIcon tipo={actionModal.proof.archivoTipo} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{actionModal.proof.archivoNombre}</p>
                  <p className="text-xs text-gray-500">{formatBytes(actionModal.proof.archivoTamano)}</p>
                </div>
                <a
                  href={`/api/payment-proofs/${actionModal.proof.id}/file`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-100 text-blue-700 text-xs font-medium hover:bg-blue-200"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Abrir
                </a>
              </div>

              {actionModal.proof.aiFlagged && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                  <span className="text-amber-600">⚠️</span>
                  <p className="text-xs text-amber-800">
                    La IA no reconoció este archivo como comprobante de pago
                    {actionModal.proof.aiReason ? `: ${actionModal.proof.aiReason}` : ''}. Revisa con cuidado antes de validar.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  {actionModal.type === 'validate' ? 'Notas (opcional)' : 'Motivo de rechazo (opcional)'}
                </Label>
                <Textarea
                  value={notasAdmin}
                  onChange={(e) => setNotasAdmin(e.target.value)}
                  placeholder={
                    actionModal.type === 'validate'
                      ? 'Ej: Pago aplicado a factura FAC-001...'
                      : 'Ej: El comprobante no es legible / no corresponde al monto...'
                  }
                  rows={3}
                  className="text-sm"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setActionModal(null); setNotasAdmin(''); }}
                disabled={actionMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => actionMutation.mutate({
                  proofId: actionModal.proof.id,
                  action: actionModal.type,
                  notas: notasAdmin,
                })}
                disabled={actionMutation.isPending}
                className={actionModal.type === 'validate'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
                }
              >
                {actionMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : actionModal.type === 'validate' ? (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                {actionModal.type === 'validate' ? 'Confirmar Validación' : 'Confirmar Rechazo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
